import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import YotiAgeVerificationSimple from "./YotiAgeVerificationSimple";
import { API_BASE } from "../utils/Instance";

export default function ProtectedRoute({ children }) {
  const { profile, authReady } = useAuth();
  const [yotiState, setYotiState] = useState({
    checking: true,
    allowed: false,
    required: false,
    error: "",
  });

  const checkYotiAccess = useCallback(async () => {
    if (!profile?.accessToken) {
      setYotiState({ checking: false, allowed: false, required: false, error: "" });
      return;
    }

    setYotiState((current) => ({ ...current, checking: true, error: "" }));

    try {
      const requirementResponse = await fetch(`${API_BASE}accounts/verify-age/requirement/`, {
        credentials: "include",
        headers: { Authorization: `Bearer ${profile.accessToken}` },
      });

      if (!requirementResponse.ok) {
        throw new Error("Unable to check Yoti verification requirement.");
      }

      const requirement = await requirementResponse.json();

      if (requirement.verification_required === false) {
        setYotiState({ checking: false, allowed: true, required: false, error: "" });
        return;
      }

      const latestResponse = await fetch(`${API_BASE}accounts/verify-age/latest/`, {
        credentials: "include",
        headers: { Authorization: `Bearer ${profile.accessToken}` },
      });

      if (latestResponse.ok) {
        const latest = await latestResponse.json();
        if (latest?.is_verified && latest?.is_over_18) {
          setYotiState({ checking: false, allowed: true, required: true, error: "" });
          return;
        }
      }

      setYotiState({ checking: false, allowed: false, required: true, error: "" });
    } catch (error) {
      console.error("Yoti gate check failed:", error);
      setYotiState({
        checking: false,
        allowed: false,
        required: true,
        error: error.message || "Unable to check Yoti verification status.",
      });
    }
  }, [profile?.accessToken]);

  useEffect(() => {
    if (!authReady || !profile?.accessToken) {
      return;
    }

    checkYotiAccess();
  }, [authReady, profile?.accessToken, checkYotiAccess]);

  if (!authReady) {
    return (
      <div className="flex items-center justify-center h-screen text-gray-400">
        Loading...
      </div>
    );
  }

  if (!profile?.accessToken) {
    return <Navigate to="/login" replace />;
  }

  if (yotiState.checking) {
    return (
      <div className="flex items-center justify-center h-screen bg-black text-gray-400">
        Checking verification...
      </div>
    );
  }

  if (yotiState.required && !yotiState.allowed) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-xl">
          {yotiState.error && (
            <div className="mb-4 rounded border border-red-700 bg-red-900/30 p-3 text-sm text-red-100">
              {yotiState.error}
              <button
                type="button"
                onClick={checkYotiAccess}
                className="ml-3 text-[#CEB46A] underline"
              >
                Retry
              </button>
            </div>
          )}
          <YotiAgeVerificationSimple
            onCompletion={(result) => {
              if (result?.verification_skipped || result?.is_over_18) {
                checkYotiAccess();
              }
            }}
          />
        </div>
      </div>
    );
  }

  return children;
}
